// What a view shows of a roll while its die still tumbles. The flow gives
// what the die gives at the click: the energy, the scroll, and the lines of
// the log. A view shows each of them when the die lands, so nothing tells the
// roll before the die does. Rob, 2026-10-12: "we should hold any new UI info
// that's contingent on the roll until the roll animation completes". It held
// only the energy until then, and this file was `energyShown.ts`.

import type { Side } from '../engine/types';
import { type Slot, slotsOf } from '../game/casters';
import { energyOf, type GameState, type LogEntry, type RollRecord } from '../game/state';
import { heldHaul } from './captureEnergy';
import { undelivered } from './energyDelivery';
import { stageStore } from './stage';

/** The last roll of the state, while its die tumbles on the stage. Null at any other time. */
export const rollInTheAir = (state: GameState): RollRecord | null => {
  const stage = stageStore.get();
  const roll = stage.roll;
  return stage.phase === 'spinning' && roll !== null && roll === state.lastRoll ? roll : null;
};

/**
 * The energy of the side, less what a die that still tumbles gives it, and
 * less the coins of a die that landed which did not drop into a pip yet.
 */
export const shownEnergy = (state: GameState, side: Side): number => {
  // The energy of a capture shows when its coins drop. See `captureEnergy.ts`.
  const energy = energyOf(state, side) - heldHaul(side);
  const { phase, roll } = stageStore.get();
  if (roll === null || roll !== state.lastRoll || roll.caster !== side) return energy;
  const held = phase === 'spinning' ? (roll.gain ?? 0) : undelivered(roll);
  return energy - held;
};

/**
 * The slots of the side. A scroll from a die that still tumbles is not in
 * its slot yet: it is the last scroll the side holds, and that slot shows
 * empty.
 */
export const shownSlots = (state: GameState, side: Side): Slot[] => {
  const slots = slotsOf(state, side);
  const roll = rollInTheAir(state);
  // `gain` is null for a roll of the wheel, whose spell is no scroll.
  if (roll?.caster !== side || roll.gain === null || roll.spellId === null) return slots;
  let newest = -1;
  slots.forEach((slot, index) => {
    if (slot.kind === 'scroll' && slot.spellId !== null) newest = index;
  });
  return slots.map((slot, index) =>
    index === newest ? ({ kind: 'scroll', spellId: null } satisfies Slot) : slot,
  );
};

/** The lines of the log, less the lines a die that still tumbles wrote. */
export const shownLog = (state: GameState): readonly LogEntry[] => {
  const roll = rollInTheAir(state);
  return roll === null ? state.log : state.log.filter((entry) => entry.id < roll.firstLogId);
};
